import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { STANDARDS_ALIGNMENT_REPORT_EXPERIMENT } from "@/state-standards/constants";
import { getCanAccessStandardsAlignmentReport } from "@/state-standards/getCanAccessStandardsAlignmentReport";
import useUser from "@/users/useUser";

/**
 * useCanAccessStandardsAlignmentReport() - Single source of truth for Standards
 * Alignment Report access on the client.
 * @description The `/state-standards` page and the Content Library nav link both
 * consume this hook so their gates cannot drift apart. Reads the user off v2
 * `useUser()` rather than v3 `useGetUserProperties()` — the source SSR uses,
 * hydrated synchronously, so the nav href does not flicker on first paint.
 *
 * @returns `canAccessStandardsAlignmentReport` — whether the viewer is eligible
 * (the conditions are OR-ed, so a resolved segment grants access even while the
 * user loads), and `isLoading` — whether the user is still resolving.
 */
const useCanAccessStandardsAlignmentReport = () => {
  const [standardsSegment, { error: segmentError }] =
    useGetUserExperimentSegment(STANDARDS_ALIGNMENT_REPORT_EXPERIMENT);
  const { data: user, loading: isLoadingUser } = useUser();

  return {
    canAccessStandardsAlignmentReport: getCanAccessStandardsAlignmentReport({
      features: user?.features,
      experimentSegment: standardsSegment,
    }),
    // Segments are SSR-seeded and never report loading, so only the user can be
    // pending — and a segments failure must not park an unhydrated viewer on a
    // spinner. `useUser` seeds `loading: true` even with `data` already there.
    isLoading: Boolean(isLoadingUser && !user) && !segmentError,
  };
};

export default useCanAccessStandardsAlignmentReport;
